/**
 * React Query hook for Case Tags (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { getCaseTag } from 'services/case/caseServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches case tags data
 *
 * @param {boolean} isLead - Whether to fetch lead tags (default: false)
 * @returns {Promise<Array>} - Array of tags
 */
const fetchCaseTags = async (isLead = false) => {
  try {
    const response = await getCaseTag(isLead);
    return response?.data || [];
  } catch (error) {
    console.error('Error fetching case tags:', error);
    throw error;
  }
};

/**
 * Hook to fetch case tags with React Query
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLead - Whether to fetch lead tags (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - tagList, isTagsLoading, error, refetch, isFetching
 */
export const useCaseTagsQuery = ({ isLead = false, enabled = true } = {}) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.caseTags.list(isLead),
    queryFn: () => fetchCaseTags(isLead),
    enabled,
    staleTime: 15 * 60 * 1000, // Consider data stale after 15 minutes (tags change less frequently)
    gcTime: 20 * 60 * 1000, // Keep in cache for 20 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component mounts - use cache
    retry: 2,
  });

  return {
    tagList: data || [],
    isTagsLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
